import { Download, X } from "lucide-react";

import { Button } from "../../../../../components/ui/button";
import { type ViewedAnalyticsOutput } from "../../../../../hooks/audit/useAnalyticsOutputFiles";
import { type AnalyticsRunOutputSummary } from "../../../../../models/audit/analyticsWorkflow";
import FileViewer from "../../../../extraction/viewExtraction/FileViewer";

interface AnalyticsOutputViewerPaneProps {
  viewed: ViewedAnalyticsOutput;
  onClose: () => void;
  /** Mints a fresh URL and hands the file over, as the row's own button does. */
  onDownload: (output: AnalyticsRunOutputSummary) => void;
  /** File ids whose URL is being minted. */
  pendingFileIds: ReadonlySet<string>;
}

/**
 * A published output open in the fold-out's viewer pane, beside the review that
 * lists it.
 *
 * The pane is the step card's otherwise, and the two take turns: opening a file
 * closes the card, and picking a step closes the file. Closing the file hands
 * the pane back to the card viewer's own empty state.
 *
 * **Download stays one press away**, minted afresh for the reason the sheet's
 * dialog gives: the URL the viewer was opened with was granted for the view.
 */
export function AnalyticsOutputViewerPane({
  viewed,
  onClose,
  onDownload,
  pendingFileIds,
}: AnalyticsOutputViewerPaneProps) {
  return (
    <div
      className="flex h-full min-h-0 flex-col"
      data-testid="analytics-output-viewer-pane"
    >
      <div className="flex shrink-0 items-center gap-2 border-b border-stone-200 px-4 py-2">
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">
            {viewed.output.fileName}
          </div>
          {viewed.output.description !== null && (
            <div className="truncate text-xs text-font-secondary">
              {viewed.output.description}
            </div>
          )}
        </div>
        <Button
          variant="outline"
          size="sm"
          aria-label={`Download ${viewed.output.fileName}`}
          disabled={pendingFileIds.has(viewed.output.fileId)}
          onClick={() => onDownload(viewed.output)}
        >
          <Download className="mr-2 h-4 w-4" />
          Download
        </Button>
        <Button
          variant="ghost"
          size="icon"
          aria-label={`Close ${viewed.output.fileName}`}
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </Button>
      </div>
      <div className="min-h-0 flex-1 overflow-hidden bg-white">
        <FileViewer
          // Keyed by the grant, so a second file opened over the first starts
          // its viewer from nothing rather than from the last file's state.
          key={viewed.url}
          signedUrl={viewed.url}
          fileType={viewed.fileType}
          fileName={viewed.output.fileName}
        />
      </div>
    </div>
  );
}
